import { FC } from 'react';
import { Column } from 'react-table';
import { useTranslation } from 'react-i18next';

import { useActiveClientContext } from 'store/active-client';

import { formatDate } from 'utils/helpers/formatters';
import { buildClientEntity } from 'utils/helpers/client';
import FetchTable from 'components/table/FetchTable';

import { ISelectOptionProps } from 'components/form/types';
import { IFilterOptions } from 'components/table/types';
import TooltipWrapper from 'components/TooltipWrapper';
import { IMyLeave } from '../types';

interface IProps {
  setQueryString?: (queryString: string) => void;
  filters?: {
    [key: string]: any;
    name: keyof IFilterOptions;
    placeholder?: string;
    options?: ISelectOptionProps[];
  }[];
}

const UpcomingLeaves: FC<IProps> = ({ setQueryString, filters }) => {
  const { client } = useActiveClientContext();
  const { t } = useTranslation();

  // Sign document as employee
  const columns: Array<Column<IMyLeave>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: t('profile.name') || '',
      accessor: 'username',
      width: '33%',
      Cell: row => {
        return row.value || '';
      },
    },
    {
      Header: t('leaves.group') || '',
      accessor: 'user_groups',
      width: '33%',
      Cell: ({ value, row }: { value: any; row: any }) => {
        const MAX_LENGTH = 20;
        const fullGroups = value.join(', ');
        const isTooLong = fullGroups.length > MAX_LENGTH;
        const groupsPreview = isTooLong ? `${fullGroups.substr(0, MAX_LENGTH)}...` : fullGroups;

        return (
          <TooltipWrapper
            key={row.index}
            id="tooltip-groups"
            overlay={fullGroups}
          >
            {groupsPreview}
          </TooltipWrapper>
        );
      },
    },
    {
      Header: t('leaves.dateRange') || '', // Use your translation for 'Date Range'
      id: 'date_range', // Custom ID for the new column
      width: '24%',
      Cell: ({ row }) => {
        const dateFrom = formatDate(new Date(row.original.date_from), 'dd.mm.YYY');
        const dateTo = formatDate(new Date(row.original.date_to), 'dd.mm.YYY');
        return <>{`${dateFrom} - ${dateTo}`}</>;
      },
    },
    {
      Header: t('leaves.status') || '',
      accessor: 'status',
      width: '10%',
      Cell: ({ value }) => {
        const statuses: any = {
          initiated: t('leaves.initiated'),
          confirmed: t('leaves.confirmed'),

          pending: t('leaves.inprogress'),
          signing: t('leaves.inprogress'),
          approved: t('leaves.inprogress'),
          employeeRejectInitiated: t('leaves.inprogress'),
          inprogress: t('leaves.inprogress'),

          rejected: t('leaves.rejected'),
          cancelled: t('leaves.cancelled'),
        };
        return statuses[value] || '';
      },
    },
  ];

  return (
    <FetchTable<IMyLeave>
      entityName={buildClientEntity('colleague-leaves', client)}
      entityEndpoint="leaves/colleagues"
      filters={filters}
      columns={columns}
      pagination
      setQueryString={setQueryString}
    />
  );
};

export default UpcomingLeaves;
